import './AjaxLeftRightSelect.less';
import React, {useState, useEffect, useRef} from 'react';
import Spinner from '../spinner/Spinner';
import {translate} from "../../i18n";
import Icon from "../icon/Icon";
import Request from "../../api/Request";

export default function AjaxLeftRightSelect({
    ItemsSearchParcel,
    ItemsFetchParcel,
    value: forcedValue,
    defaultValue = [],
    onChange = value => undefined,
    title = translate('All options'),
    getSelectedSummary = selectedCount => translate('Selected (::count)', {count: selectedCount}),
    getDeselectAllText = selectedCount => translate('Deselect all'),
    renderOptionContent = (option, extras) => option.name,
    getOptionId = option => option.id,
    getOptionDisabled = (option, extras) => option.disabled,
    getOptionCaution = (option, extras) => null,
    disabled: allDisabled = false,
}) {

    const [items, setItems] = useState([]);

    const [hasMore, setHasMore] = useState(true);

    const [lastPage, setLastPage] = useState(0);

    const [selectedItems, setSelectedItems] = useState([]);

    const [isFirstSearched, setIsFirstSearched] = useState(false);

    const [isFirstFetched, setIsFirstFetched] = useState(false);

    const [isSearching, setIsSearching] = useState(false);

    const [isFetching, setIsFetching] = useState(false);

    const [searchingError, setSearchingError] = useState(null);

    const [fetchingError, setFetchingError] = useState(null);

    const [keyword, setKeyword] = useState('');

    const [keywordTimer, setKeywordTimer] = useState(null);

    const scrollViewRef = useRef(null);

    const keywordInputRef = useRef(null);

    const searchItems = (page) => {
        if (isSearching || (page > 0 && !hasMore)) {
            return;
        }

        setIsSearching(true);
        setHasMore(true);
        setSearchingError(null);

        Request.add(new ItemsSearchParcel(keyword, page))
               .then(data => {
                   const prevItems = page === 0 ? [] : items;
                   const nextItems = prevItems.concat(data.items);
                   const hasMore = data.total !== undefined
                       ? data.items.length > 0 && data.total > nextItems.length
                       : data.items.length > 0;

                   setLastPage(page);
                   setHasMore(hasMore);
                   setItems(nextItems);
               })
               .catch(error => {
                   setSearchingError(error.getMessage());
               })
               .finally(() => {
                   setIsSearching(false);

                   if (!isFirstSearched) {
                       setIsFirstSearched(true);
                   }
               });
    };

    const fetchItems = (value) => {
        setIsFetching(true);
        setFetchingError(null);

        Request.add(new ItemsFetchParcel(value))
               .then(data => {
                   setSelectedItems(data.items);
               })
               .catch(error => {
                   setFetchingError(error.getMessage());
               })
               .finally(() => {
                   setIsFetching(false);

                   if (!isFirstFetched) {
                       setIsFirstFetched(true);
                   }
               });
    };

    useEffect(() => {
        searchItems(0);
    }, []);

    useEffect(() => {
        if (!isFirstSearched) {
            return;
        }

        searchItems(0);

        if (keywordInputRef.current.value !== keyword) {
            keywordInputRef.current.value = keyword;
        }
    }, [keyword]);

    useEffect(() => {
        if (defaultValue.length === 0) {
            setSelectedItems([]);
            setIsFirstFetched(true);
            return;
        }

        fetchItems(defaultValue);
    }, []);

    useEffect(() => {
        if (!isFirstFetched) {
            return;
        }

        onChange(selectedItems.map(t => t.id));
    }, [selectedItems]);

    useEffect(() => {
        if (forcedValue === undefined) {
            return;
        }

        if (forcedValue.length === 0) {
            setSelectedItems([]);
            return;
        }

        fetchItems(forcedValue);
    }, [forcedValue]);

    const onScroll = () => {
        if (!isFirstSearched) {
            return;
        }

        const sv = scrollViewRef.current;

        if (sv == null) {
            return;
        }

        if (sv.clientHeight + sv.scrollTop < sv.scrollHeight - 2) {
            return;
        }

        searchItems(lastPage + 1);
    };

    const delaySetKeyword = (keyword) => {
        if (keywordTimer !== null) {
            clearTimeout(keywordTimer);
        }

        setKeywordTimer(setTimeout(() => {
            setKeyword(keyword);
            setKeywordTimer(null);
        }, 500));
    };

    useEffect(() => {
        if (!isFirstSearched) {
            return;
        }

        const sv = scrollViewRef.current;

        if (sv === null) {
             return;
        }

        if (sv.scrollHeight > sv.clientHeight) {
            return;
        }

        searchItems(lastPage + 1);
    }, [items, isFirstSearched]);

    const createOptionExtras = (selected, inLeftColumn) => {
        return {selected, inLeftColumn};
    };

    const select = (item) => {
        if (!selectedItems.some(t => getOptionId(t) === getOptionId(item))) {
            setSelectedItems(selectedItems.concat([item]));
        }
    };

    const deselect = (item) => {
        const newSelectedItems = selectedItems.filter(t => getOptionId(t) !== getOptionId(item));

        if (newSelectedItems.length !== selectedItems.length) {
            setSelectedItems(newSelectedItems);
        }
    };

    const deselectAll = () => {
        const keptItems = selectedItems.filter(item => {
            const extras = createOptionExtras(true, false);
            return getOptionDisabled(item, extras);
        });

        setSelectedItems(keptItems);
    };

    const renderOption = (item, inLeftColumn) => {
        const itemId = getOptionId(item);
        const selected = selectedItems.some(t => getOptionId(t) === itemId);
        const extras = createOptionExtras(selected, inLeftColumn);
        const selectable = inLeftColumn && !disabled;
        const highlighted = selectable && selected && inLeftColumn;
        const disabled = allDisabled || getOptionDisabled(item, extras);
        const caution = getOptionCaution(item, extras);

        return <li
            key={itemId}
            onClick={inLeftColumn && !disabled ? () => select(item) : undefined}
            className={[
                selectable && 'selectable',
                selected && 'selected',
                disabled && 'disabled',
                highlighted && 'highlighted',
            ].filter(Boolean).join(' ')}
        >
            <div className="option">
                {renderOptionContent(item, extras)}
            </div>
            {
                caution !== null &&
                <button
                    type="button"
                    className="caution-button"
                    aria-controls="tooltip-click-to-hold"
                    aria-label={caution}
                >
                    <Icon name="caution-circle"/>
                </button>
            }
            {
                !disabled &&
                <button
                    type="button"
                    onClick={!inLeftColumn && !disabled ? () => deselect(item) : undefined}
                    className={inLeftColumn ? 'select-button' : 'deselect-button'}
                >
                    {
                        inLeftColumn
                            ? <Icon name="arrow-right"/>
                            : <Icon name="times"/>
                    }
                </button>
            }
        </li>;
    };

    const renderLoading = () => {
        return <div className="loading">
            <Spinner/>
            <div className="text">{translate('Loading...')}</div>
        </div>;
    }

    const renderError = (error) => {
        return <div className="error">
            <Icon name="caution-circle"/>
            <div className="text">{error}</div>
        </div>;
    };

    const renderEmpty = () => {
        return <div className="empty">
            {translate('Empty list')}
        </div>;
    };

    return <div className="ajax-left-right-select">
        <table>
            <thead>
            <tr>
                <td className="left">
                    <div className="inner">
                        <div className="search-box">
                            <input
                                className="keyword-input"
                                type="text"
                                placeholder={title}
                                onChange={ev => delaySetKeyword(ev.target.value)}
                                ref={keywordInputRef}
                            />
                            <Icon name="search"/>
                        </div>
                    </div>
                </td>
                <td className="right">
                    <div className="inner">
                        <div className="selected-summary">
                            {getSelectedSummary(selectedItems.length)}
                        </div>
                        {
                            !allDisabled &&
                            selectedItems.length > 0 &&
                            <button
                                type="button"
                                className="deselect-all-button"
                                onClick={() => deselectAll()}
                            >
                                {getDeselectAllText(selectedItems.length)}
                            </button>
                        }
                    </div>
                </td>
            </tr>
            </thead>
            <tbody>
            <tr>
                <td className="left">
                    <div className="inner">
                        <div
                            className="options"
                            onScroll={onScroll}
                            ref={scrollViewRef}
                        >
                            <ul>
                                {
                                    items.map(option => renderOption(option, true))
                                }
                            </ul>
                        </div>
                        {
                            isSearching &&
                            renderLoading()
                        }
                        {
                            searchingError !== null &&
                            renderError(searchingError)
                        }
                        {
                            !isSearching &&
                            searchingError === null &&
                            items.length === 0 &&
                            renderEmpty()
                        }
                    </div>
                </td>
                <td className="right">
                    <div className="inner">
                        <div className="options">
                            <ul>
                                {selectedItems.map(option => renderOption(option, false))}
                            </ul>
                        </div>
                        {
                            isFetching &&
                            renderLoading()
                        }
                        {
                            fetchingError !== null &&
                            renderError(fetchingError)
                        }
                    </div>
                </td>
            </tr>
            </tbody>
        </table>
    </div>;
}
